import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Form, ListGroup, Spinner, Alert } from 'react-bootstrap';
import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { createLogger } from '../../utils/logger';
import { ShoppingListItem, ShoppingListWritePayload, SHOPPING_LIST_MAX_ITEMS } from '../../types/shoppingList';
import { createShoppingList, updateShoppingList } from '../../services/shoppingListService';
import { mergeListItems } from '../../utils/shoppingListUtils';
import ShoppingListProductPicker from './ShoppingListProductPicker';
import './ShoppingListFormModal.css';

const logger = createLogger('ShoppingListFormModal');

interface SupermarketOption {
  id: string;
  display_name?: string;
}

interface ShoppingListFormModalProps {
  show: boolean;
  mode: 'create' | 'edit';
  userId: string;
  listId?: string;
  initialName: string;
  initialSupermarketId: string;
  initialSupermarketName: string;
  initialNotificationDate: Timestamp | null;
  initialItems: ShoppingListItem[];
  onHide: () => void;
  onSaved: (message: string) => void;
}

function timestampToDateInputValue(timestamp: Timestamp | null): string {
  if (!timestamp) return '';
  const date = timestamp.toDate();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export default function ShoppingListFormModal({
  show,
  mode,
  userId,
  listId,
  initialName,
  initialSupermarketId,
  initialSupermarketName,
  initialNotificationDate,
  initialItems,
  onHide,
  onSaved,
}: ShoppingListFormModalProps) {
  const [name, setName] = useState(initialName);
  const [supermarketId, setSupermarketId] = useState(initialSupermarketId);
  const [notificationDate, setNotificationDate] = useState(
    timestampToDateInputValue(initialNotificationDate)
  );
  const [items, setItems] = useState<ShoppingListItem[]>(initialItems);
  const [supermarkets, setSupermarkets] = useState<SupermarketOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const wasOpenRef = useRef(false);

  const fetchSupermarkets = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'Supermarkets'));
      const options = snapshot.docs.map((supermarketDoc) => ({
        id: supermarketDoc.id,
        display_name: supermarketDoc.data().display_name,
      }));
      options.sort((a, b) => (a.display_name || a.id).localeCompare(b.display_name || b.id));
      setSupermarkets(options);
    } catch (fetchError) {
      logger.error('Failed to fetch supermarkets', fetchError);
    }
  }, []);

  useEffect(() => {
    if (show && !wasOpenRef.current) {
      setName(initialName);
      setSupermarketId(initialSupermarketId);
      setNotificationDate(timestampToDateInputValue(initialNotificationDate));
      setItems(initialItems.map((item) => ({ ...item })));
      setError('');
      setSaving(false);
      fetchSupermarkets();
    }
    wasOpenRef.current = show;
  }, [
    show,
    initialName,
    initialSupermarketId,
    initialNotificationDate,
    initialItems,
    fetchSupermarkets,
  ]);

  const handleQuantityChange = (productId: string, value: string) => {
    const parsed = parseInt(value, 10);
    setItems((previous) =>
      previous.map((item) =>
        item.productId === productId
          ? { ...item, quantity: isNaN(parsed) ? 0 : parsed }
          : item
      )
    );
  };

  const handleRemoveItem = (productId: string) => {
    setItems((previous) => previous.filter((item) => item.productId !== productId));
  };

  const handleAddProduct = (product: ShoppingListItem) => {
    setItems((previous) => {
      const merged = mergeListItems(previous, [product]);
      if (merged.length > SHOPPING_LIST_MAX_ITEMS) {
        setError(`A shopping list can have at most ${SHOPPING_LIST_MAX_ITEMS} products.`);
        return previous;
      }
      setError('');
      return merged;
    });
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Please enter a name for the shopping list.');
      return;
    }
    if (!supermarketId) {
      setError('Please choose a supermarket for the shopping list.');
      return;
    }
    if (items.length === 0) {
      setError('A shopping list needs at least one product.');
      return;
    }
    const invalidItem = items.find((item) => !Number.isInteger(item.quantity) || item.quantity < 1);
    if (invalidItem) {
      setError(`Quantity for "${invalidItem.name}" must be at least 1.`);
      return;
    }

    const selectedSupermarket = supermarkets.find((option) => option.id === supermarketId);
    const basePayload = {
      name: trimmedName,
      supermarketId,
      supermarketName:
        selectedSupermarket?.display_name || initialSupermarketName || 'Unknown Supermarket',
      notificationDate: notificationDate
        ? Timestamp.fromDate(new Date(`${notificationDate}T09:00:00`))
        : null,
      items: items.map((item) => ({
        productId: item.productId,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
      })),
    };

    setSaving(true);
    setError('');
    try {
      if (mode === 'edit' && listId) {
        await updateShoppingList(listId, basePayload);
        onSaved(`Shopping list "${trimmedName}" was updated.`);
      } else {
        const payload: ShoppingListWritePayload = { ...basePayload, userId };
        await createShoppingList(payload);
        onSaved(`Shopping list "${trimmedName}" was saved.`);
      }
    } catch (saveError) {
      logger.error('Failed to save shopping list', saveError);
      const code = (saveError as { code?: string })?.code;
      setError(
        code === 'permission-denied'
          ? 'Saving shopping lists is not enabled for your account yet. Please try again later. (permission-denied)'
          : `Failed to save the shopping list. Please try again.${code ? ` (${code})` : ''}`
      );
      setSaving(false);
    }
  };

  const supermarketOptions = [...supermarkets];
  if (
    initialSupermarketId &&
    !supermarketOptions.some((option) => option.id === initialSupermarketId)
  ) {
    supermarketOptions.unshift({
      id: initialSupermarketId,
      display_name: initialSupermarketName || initialSupermarketId,
    });
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      dialogClassName="shopping-list-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === 'edit' ? 'Edit Shopping List' : 'Save Cart as Shopping List'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group className="mb-3" controlId="shoppingListName">
          <Form.Label>List Name</Form.Label>
          <Form.Control
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Weekly groceries"
            maxLength={100}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="shoppingListSupermarket">
          <Form.Label>Supermarket</Form.Label>
          <Form.Select
            value={supermarketId}
            onChange={(event) => setSupermarketId(event.target.value)}
          >
            <option value="" disabled>
              Choose a supermarket
            </option>
            {supermarketOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.display_name || option.id}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Availability is checked against the selected supermarket.
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-3" controlId="shoppingListNotificationDate">
          <Form.Label>Notification Date (optional)</Form.Label>
          <Form.Control
            type="date"
            value={notificationDate}
            min={timestampToDateInputValue(Timestamp.fromDate(new Date()))}
            onChange={(event) => setNotificationDate(event.target.value)}
          />
          <Form.Text className="text-muted">
            Optional reminder date shown on your shopping list. In-app only — no push or email
            reminder is sent yet.
          </Form.Text>
        </Form.Group>
        <ShoppingListProductPicker
          supermarketId={supermarketId}
          existingItems={items}
          onAddProduct={handleAddProduct}
          disabled={saving}
        />
        <Form.Label>Products on this list</Form.Label>
        {items.length === 0 ? (
          <p className="text-muted">All products have been removed from this list.</p>
        ) : (
          <ListGroup variant="flush" className="shopping-list-items-editor">
            {items.map((item) => (
              <ListGroup.Item
                key={item.productId}
                className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between px-0 gap-2"
              >
                <div className="flex-grow-1 me-sm-2">
                  <div className="fw-semibold text-break">{item.name}</div>
                  <small className="text-muted">${item.price.toFixed(2)} each</small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="number"
                    min={1}
                    value={item.quantity}
                    onChange={(event) => handleQuantityChange(item.productId, event.target.value)}
                    className="shopping-list-qty-input"
                    size="sm"
                    aria-label={`Quantity for ${item.name}`}
                  />
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleRemoveItem(item.productId)}
                  >
                    Remove
                  </Button>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner as="span" size="sm" className="me-2" />
              Saving...
            </>
          ) : mode === 'edit' ? (
            'Save Changes'
          ) : (
            'Save Shopping List'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
